import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, ChevronRight, FolderKanban, TrendingUp, DollarSign, Sun,
} from "lucide-react";
import KpiCard, { KpiGrid } from "@/components/KpiCard";

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [proj, cli] = await Promise.all([
        api.get("/projects"),
        api.get("/clients"),
      ]);
      setProjects(proj.data);
      setClients(cli.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => {
    const m = new Map();
    for (const c of clients) m.set(c.user_id, c);
    return m;
  }, [clients]);

  const STATUS_META = {
    ordered:          { label: "Ordered",         className: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
    in_process:       { label: "In progress",     className: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300" },
    awaiting_arrival: { label: "Awaiting parts",  className: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
    scheduling:       { label: "Scheduling",      className: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300" },
    scheduled:        { label: "Scheduled",       className: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300" },
    installed:        { label: "Installed",       className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
    completed:        { label: "Completed",       className: "bg-emerald-600 text-white" },
    cancelled:        { label: "Cancelled",       className: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" },
  };

  const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "ordered", label: "Ordered" },
    { key: "in_process", label: "In progress" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
  ];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter === "active" && ["completed", "cancelled"].includes(p.status)) return false;
      if (["ordered", "in_process", "completed", "cancelled"].includes(statusFilter) && p.status !== statusFilter) return false;
      if (q) {
        const c = clientMap.get(p.user_id);
        const hay = `${p.title || ""} ${p.customer_code || ""} ${c?.name || ""} ${c?.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [projects, clientMap, query, statusFilter]);

  const kpis = useMemo(() => {
    const active = projects.filter((p) => !["completed", "cancelled"].includes(p.status)).length;
    const value = projects.reduce((a, p) => a + (Number(p.amount) || 0), 0);
    const kwSum = projects
      .filter((p) => (p.project_type || "").startsWith("solar"))
      .reduce((a, p) => a + (Number(p.kw_target) || 0), 0);
    return { total: projects.length, active, value, kwSum };
  }, [projects]);

  return (
    <div data-testid="admin-projects">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline">Operations</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Projects</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Every project created for a client — click to open the execution page, reserve materials, and update status.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <KpiGrid className="mb-6">
        <KpiCard label="Total projects" value={kpis.total} tone="neutral" icon={FolderKanban} />
        <KpiCard label="Active" value={kpis.active} tone={kpis.active > 0 ? "positive" : "neutral"} icon={TrendingUp} />
        <KpiCard label="Pipeline value" value={kpis.value} money decimals={0} tone="neutral" icon={DollarSign} />
        <KpiCard label="Solar kW booked" value={kpis.kwSum} decimals={1} suffix=" kW" tone="neutral" icon={Sun} />
      </KpiGrid>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              data-testid={`projects-tab-${t.key}`}
              className={`px-3 h-10 text-xs font-semibold whitespace-nowrap transition ${
                statusFilter === t.key
                  ? "bg-[#0A2540] text-white"
                  : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, client name, or PB-code"
            data-testid="projects-search"
            className="w-full pl-9 pr-3 h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 text-sm placeholder:text-[#94A3B8]"
          />
        </div>
      </div>

      <div className="card-pb overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#475569] dark:text-slate-400">Loading projects…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="projects-empty">
            <p className="text-sm text-[#475569] dark:text-slate-400">
              No projects match this filter. Create one from any client's drawer via <strong>+ New project</strong>.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-slate-50 dark:bg-slate-900/60 text-left">
                <tr className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3 text-right">Materials</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const c = clientMap.get(p.user_id);
                  const meta = STATUS_META[p.status] || STATUS_META.ordered;
                  const reserved = (p.materials_reserved || []).length;
                  return (
                    <tr
                      key={p.project_id}
                      className="border-t border-[#E2E8F0] dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                      data-testid={`admin-project-${p.project_id}`}
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/projects/${p.project_id}`}
                          className="font-medium text-[#0A2540] dark:text-white hover:text-[#C9A356]"
                          data-testid={`open-detail-${p.project_id}`}
                        >
                          {p.title}
                        </Link>
                        <div className="text-[11px] font-mono text-[#94A3B8] mt-0.5">
                          {p.project_number ? `#${p.project_number}` : p.project_id}
                          {p.kw_target ? ` · ${p.kw_target} kW` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#0A2540] dark:text-white">{c?.name || "—"}</div>
                        <div className="text-[11px] font-mono text-[#C9A356]">{p.customer_code || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${meta.className}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#475569] dark:text-slate-400 text-xs">
                        {(p.project_type || "").replace(/_/g, " ")}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-[#0A2540] dark:text-white">
                        ${Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {reserved > 0 ? (
                          <div className="relative group inline-block">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 cursor-help" data-testid={`reserved-badge-${p.project_id}`}>
                              {reserved} reserved
                            </span>
                            <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block w-64 rounded-lg border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-3 text-left">
                              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-2">Reserved kit</p>
                              <ul className="space-y-1.5">
                                {(p.materials_reserved || []).map((r, i) => (
                                  <li key={i} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-[#0A2540] dark:text-white truncate">{r.material_name || r.material_id}</span>
                                    <span className="font-mono tabular-nums text-[#C9A356] shrink-0">× {r.qty}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/projects/${p.project_id}`}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#C9A356]/15 text-[#C9A356] hover:bg-[#C9A356]/25 text-xs font-semibold"
                        >
                          Open <ChevronRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-[#E2E8F0] dark:divide-slate-800">
              {filtered.map((p) => {
                const c = clientMap.get(p.user_id);
                const meta = STATUS_META[p.status] || STATUS_META.ordered;
                const reserved = (p.materials_reserved || []).length;
                return (
                  <li key={p.project_id} className="p-4" data-testid={`admin-project-card-${p.project_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <Link
                          to={`/admin/projects/${p.project_id}`}
                          className="font-medium text-[#0A2540] dark:text-white hover:text-[#C9A356]"
                        >
                          {p.title}
                        </Link>
                        <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-0.5">
                          {c?.name || "—"} · <span className="font-mono text-[#C9A356]">{p.customer_code || "—"}</span>
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${meta.className} shrink-0`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="text-[#475569] dark:text-slate-400">
                        {(p.project_type || "").replace(/_/g, " ")}
                        {p.kw_target ? ` · ${p.kw_target} kW` : ""}
                        {reserved > 0 ? ` · ${reserved} reserved` : ""}
                      </div>
                      <span className="font-semibold text-[#0A2540] dark:text-white tabular-nums">
                        ${Number(p.amount || 0).toLocaleString()}
                      </span>
                    </div>
                    <Link
                      to={`/admin/projects/${p.project_id}`}
                      className="mt-3 inline-flex items-center justify-center gap-1 w-full h-9 rounded-md bg-[#C9A356]/15 text-[#C9A356] hover:bg-[#C9A356]/25 text-xs font-semibold"
                    >
                      Open execution page <ChevronRight className="w-3 h-3" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
